import { API_BASE_URL, API_KEY, getApiHeaders } from '../config/api';
import { CANDIDATE_CATEGORY_BY_TYPE, Environment, ShopMediaAsset } from './premiumBundleRules';
import { BulkEnvironmentMoveRequest, BulkEnvironmentMoveResult, parseEnvironmentMoveResult } from './bulkEnvironmentMove';

/**
 * Thin wrappers over the wallet shop endpoints used by the premium bundle and sticker pack editors.
 * Same calls (and error handling) as the other product pages — see docs/wallet-shop-endpoints.md.
 */

export const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;

export const authHeaders = (): { headers: Record<string, string> } | { error: string } => {
  const token = localStorage.getItem('authToken');
  if (!token) return { error: 'No token found. Please log in.' };
  if (!API_KEY) return { error: 'Missing API key. Set REACT_APP_API_KEY in your .env file.' };
  return { headers: getApiHeaders(token) };
};

export const resolveUserId = () => (localStorage.getItem('wallet_assets_user_id') || '').trim() || 'dashboard';

const post = async (headers: Record<string, string>, lambda: string, body: unknown) => {
  const res = await fetch(`${walletApiBase}/${lambda}`, { method: 'POST', headers, body: JSON.stringify(body) });
  let json: any = {};
  try {
    json = await res.json();
  } catch {
    // non-JSON error body
  }
  if (!res.ok || json?.status === false) throw new Error(json?.message || `${lambda} failed (${res.status})`);
  return json;
};

/** Every product of one Type in one Environment, inactive and any currency included. */
export async function fetchProductsByType<T = any>(
  headers: Record<string, string>,
  type: string,
  environment: Environment
): Promise<T[]> {
  const category = CANDIDATE_CATEGORY_BY_TYPE[type];
  const json = await post(headers, 'getWalletShopProductsV2', {
    UserID: resolveUserId(),
    ...(category ? { CategoryKey: category } : {}),
    Type: type,
    IncludeInactive: true,
    IncludeAnimation: true,
    CurrencyType: 'all',
    Environment: environment,
  });
  const products = (json.data?.products as any[]) || [];
  // Category queries (e.g. STICKER) can return other types; keep only the requested one.
  return products
    .filter((product) => !product.Type || product.Type === type)
    .map((product) => ({ ...product, Environment: product.Environment || environment }));
}

export const saveWalletShopProduct = (headers: Record<string, string>, payload: Record<string, any>, isUpdate: boolean) =>
  post(headers, isUpdate ? 'updateWalletShopProduct' : 'createWalletShopProduct', payload);

export const deleteWalletShopProduct = (headers: Record<string, string>, productKey: string, environment: Environment) =>
  post(headers, 'deleteWalletShopProduct', { ProductKey: productKey, Environment: environment });

/** Presigned R2 upload (createWalletAssetUploadPresignedUrl → PUT), returns the MediaAsset to store. */
export async function uploadWalletAsset(
  headers: Record<string, string>,
  productKey: string,
  assetType: string,
  file: File,
  size?: { Width?: number; Height?: number }
): Promise<ShopMediaAsset> {
  const contentType = file.type || 'application/octet-stream';
  const json = await post(headers, 'createWalletAssetUploadPresignedUrl', {
    ProductKey: productKey,
    ContentType: contentType,
    AssetType: assetType,
  });
  const { UploadUrl, AssetUrl } = json.data || {};
  if (!UploadUrl || !AssetUrl) throw new Error('Upload URL missing from createWalletAssetUploadPresignedUrl response.');
  const upload = await fetch(UploadUrl, { method: 'PUT', body: await file.arrayBuffer() });
  if (!upload.ok) throw new Error((await upload.text()) || 'Failed to upload asset to storage');
  return {
    Type: assetType,
    Url: AssetUrl,
    MimeType: contentType,
    ...(size?.Width && size?.Height ? { Width: size.Width, Height: size.Height } : {}),
  };
}

/**
 * Moves products between dev and prod (one request — callers batch with
 * buildEnvironmentMoveRequests). Returns the normalised `data`; 400s throw with the server message.
 */
export async function bulkUpdateWalletShopProductEnvironment(
  headers: Record<string, string>,
  request: BulkEnvironmentMoveRequest
): Promise<BulkEnvironmentMoveResult> {
  const json = await post(headers, 'bulkUpdateWalletShopProductEnvironment', request);
  return parseEnvironmentMoveResult(json.data, request.ProductKeys.length);
}
